import Axios from 'axios';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Store } from '../../Store';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Accordion from 'react-bootstrap/Accordion';
import Spinner from 'react-bootstrap/Spinner';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { api } from '../../Api';

/**
 * PROFILE EDIT LIMITS
 * ===================
 * How many times a member may change each field of their own profile, for the
 * lifetime of their account. 0 locks the field outright; blank/-1 leaves it
 * unlimited, which is how the site behaved before this screen existed.
 *
 * The lower half looks up one member: how many edits they have used, and the
 * two things an admin actually wants to do about it — give them one more, or
 * wipe the counter.
 */
export default function ProfileEditLimitScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const auth = useMemo(
    () => ({ headers: { authorization: `Bearer ${userInfo.token}` } }),
    [userInfo.token]
  );

  const [loading, setLoading] = useState(0);
  const [saving, setSaving] = useState(0);
  const [fields, setFields] = useState([]);
  const [limits, setLimits] = useState({});
  const [lockedMessage, setLockedMessage] = useState('');
  const [updatedBy, setUpdatedBy] = useState(null);

  const fetchPolicy = async () => {
    try {
      setLoading(1);
      const { data } = await Axios.get(
        api + '/api/users/admin/profile-edit/policy',
        auth
      );
      setFields(data.fields || []);
      setLimits(
        Object.fromEntries((data.fields || []).map((f) => [f.key, f.limit]))
      );
      setLockedMessage(data.lockedMessage || '');
      setUpdatedBy(data.updatedBy || null);
      setLoading(0);
    } catch (error) {
      setLoading(0);
      toast.error(getError(error));
    }
  };

  useEffect(() => {
    fetchPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const groups = useMemo(() => {
    const out = [];
    for (const field of fields) {
      const existing = out.find((g) => g.title === field.group);
      if (existing) existing.fields.push(field);
      else out.push({ title: field.group, fields: [field] });
    }
    return out;
  }, [fields]);

  const setLimit = (key, value) =>
    setLimits((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    try {
      setSaving(1);
      const payload = {};
      for (const field of fields) {
        if (field.system) continue;
        const raw = limits[field.key];
        payload[field.key] = raw === '' || raw === null ? 0 : Number(raw);
      }
      const { data } = await Axios.put(
        api + '/api/users/admin/profile-edit/policy',
        { limits: payload, lockedMessage },
        auth
      );
      setFields(data.fields || []);
      setUpdatedBy(data.updatedBy || null);
      setSaving(0);
      toast.success('Profile edit limits saved.');
    } catch (error) {
      setSaving(0);
      toast.error(getError(error));
    }
  };

  /* ------------------------------------------------------------- one member */

  const [lookup, setLookup] = useState('');
  const [member, setMember] = useState(null);
  const [memberFields, setMemberFields] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(0);

  const findMember = async (key) => {
    const wanted = String(key ?? lookup).trim();
    if (!wanted) return;
    try {
      setBusy(1);
      const { data } = await Axios.get(
        api + `/api/users/admin/profile-edit/user/${wanted}`,
        auth
      );
      setMember(data.user);
      setMemberFields(data.fields || []);
      // Just the last few here; the searchable, paginated log is its own page.
      const log = await Axios.get(
        api + `/api/users/admin/profile-edit/history?userId=${data.user._id}&pageSize=10`,
        auth
      );
      setHistory(log.data.rows || []);
      setBusy(0);
    } catch (error) {
      setBusy(0);
      setMember(null);
      setMemberFields([]);
      setHistory([]);
      toast.error(getError(error));
    }
  };

  const grant = async (fieldKey, extra) => {
    try {
      setBusy(1);
      await Axios.post(
        api + '/api/users/admin/profile-edit/grant',
        { userId: member._id, field: fieldKey, extra },
        auth
      );
      toast.success(
        extra > 0
          ? `Approved ${extra} more edit(s).`
          : `Removed ${Math.abs(extra)} granted edit(s).`
      );
      await findMember(member._id);
    } catch (error) {
      setBusy(0);
      toast.error(getError(error));
    }
  };

  const resetField = async (fieldKey) => {
    if (
      !window.confirm(
        fieldKey
          ? 'Set this field back to zero edits used?'
          : 'Set every field back to zero edits used for this member?'
      )
    )
      return;
    try {
      setBusy(1);
      await Axios.post(
        api + '/api/users/admin/profile-edit/reset',
        { userId: member._id, field: fieldKey || undefined },
        auth
      );
      toast.success('Counters reset.');
      await findMember(member._id);
    } catch (error) {
      setBusy(0);
      toast.error(getError(error));
    }
  };

  const limitLabel = (field) => {
    if (field.system) return 'Locked';
    if (field.limit === -1) return 'Unlimited';
    if (field.limit === 0) return 'No edits';
    return `${field.limit} time${field.limit === 1 ? '' : 's'}`;
  };

  return (
    <div>
      <Helmet>
        <title>Profile Edit Limits</title>
      </Helmet>
      <Container className="mt-3">
        <div className="card mb-3">
          <div className="card-header">
            <i className="fa fa-lock" aria-hidden="true" /> Profile Edit Limits{' '}
            {loading ? <Spinner animation="border" size="sm" /> : null}
            <span className="ms-2 text-muted float-right" style={{ fontSize: 12 }}>
              Last updated by{' '}
              {updatedBy ? (
                <>
                  <b>{updatedBy.name}</b> ({updatedBy.username})
                </>
              ) : (
                '—'
              )}
            </span>
          </div>
          <div className="card-body">
            
            {groups.map((group) => (
              <div key={group.title} className="mb-3">
                <h6 className="mt-3">{group.title}</h6>
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th style={{ width: 160 }}>Edit limit</th>
                      <th style={{ width: 140 }}>Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.fields.map((field) => (
                      <tr key={field.key}>
                        <td>
                          {field.label}
                          {field.system ? (
                            <div style={{ fontSize: 11 }} className="text-muted">
                              The username identifies the account across the whole
                              tree, so it can only be changed by the office.
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min={-1}
                            max={1000}
                            size="sm"
                            disabled={field.system}
                            value={field.system ? 0 : limits[field.key] ?? 0}
                            onChange={(e) => setLimit(field.key, e.target.value)}
                          />
                        </td>
                        <td>
                          <Badge
                            bg={
                              field.system || field.limit === 0
                                ? 'secondary'
                                : field.limit === -1
                                ? 'warning'
                                : 'success'
                            }
                          >
                            {limitLabel(field)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ))}

            <Form.Group className="mb-3">
              <Form.Label>Message shown when a member runs out of edits</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={lockedMessage}
                onChange={(e) => setLockedMessage(e.target.value)}
              />
            </Form.Group>

            <Button variant="primary w-100" onClick={save} disabled={saving === 1}>
              {saving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Save edit limits'
              )}
            </Button>
          </div>
        </div>

        {/* ------------------------------------------------------ one member */}
        <div className="card mb-5">
          <div className="card-header">
            <i className="fa fa-user" aria-hidden="true" /> A member's edits
          </div>
          <div className="card-body">
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                placeholder="Username or user id"
                value={lookup}
                onChange={(e) => setLookup(e.target.value)}
                onKeyDown={(e) => (e.key === 'Enter' ? findMember() : null)}
              />
              <Button
                variant="outline-primary"
                onClick={() => findMember()}
                disabled={busy === 1}
              >
                {busy ? <Spinner animation="border" size="sm" /> : 'Look up'}
              </Button>
            </div>

            {member ? (
              <>
                <p className="mb-2">
                  <b>{member.name}</b> ({member.username}) — {member.phone}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-3"
                    onClick={() => resetField(null)}
                  >
                    Reset every counter
                  </Button>
                </p>

                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Their state</th>
                      <th>Limit</th>
                      <th>Used</th>
                      <th>Left</th>
                      <th>Extra granted</th>
                      <th style={{ width: 190 }}>Approve more</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberFields.map((field) => (
                      <tr key={field.key}>
                        <td>{field.label}</td>
                        <td>
                          <Badge
                            bg={
                              field.adminCanEdit
                                ? field.canEdit
                                  ? 'success'
                                  : 'info'
                                : 'danger'
                            }
                          >
                            {field.system
                              ? 'office only'
                              : !field.adminCanEdit
                              ? 'limit reached'
                              : field.canEdit
                              ? 'empty — member can fill'
                              : 'filled — admin only'}
                          </Badge>
                        </td>
                        <td>{field.unlimited ? '∞' : field.limit}</td>
                        <td>{field.used}</td>
                        <td>
                          <Badge
                            bg={
                              field.unlimited
                                ? 'warning'
                                : field.remaining > 0
                                ? 'success'
                                : 'secondary'
                            }
                          >
                            {field.unlimited ? '∞' : field.remaining}
                          </Badge>
                        </td>
                        <td>{field.granted || '—'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-success"
                            disabled={field.system || busy === 1}
                            onClick={() => grant(field.key, 1)}
                          >
                            +1
                          </Button>{' '}
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={field.system || busy === 1}
                            onClick={() => grant(field.key, -1)}
                          >
                            -1
                          </Button>{' '}
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={field.system || busy === 1 || !field.used}
                            onClick={() => resetField(field.key)}
                          >
                            Reset
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Accordion className="mt-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      Recent changes ({history.length})
                    </Accordion.Header>
                    <Accordion.Body>
                      <Table striped bordered responsive size="sm">
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Field</th>
                            <th>From</th>
                            <th>To</th>
                            <th>By</th>
                            <th>Where</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((row) => (
                            <tr key={row._id}>
                              <td>{new Date(row.createdAt).toLocaleString()}</td>
                              <td>
                                {row.label || row.field}{' '}
                                <Badge
                                  bg={
                                    row.action === 'edit'
                                      ? 'primary'
                                      : row.action === 'admin'
                                      ? 'info'
                                      : row.action === 'grant'
                                      ? 'success'
                                      : 'secondary'
                                  }
                                >
                                  {row.action}
                                </Badge>
                              </td>
                              <td style={{ maxWidth: 180, overflowWrap: 'anywhere' }}>
                                {row.from || '—'}
                              </td>
                              <td style={{ maxWidth: 180, overflowWrap: 'anywhere' }}>
                                {row.to || '—'}
                              </td>
                              <td>{row.changedByName || '—'}</td>
                              <td>{row.source}</td>
                            </tr>
                          ))}
                          {!history.length ? (
                            <tr>
                              <td colSpan={6} className="text-center text-muted">
                                Nothing changed yet.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </Table>
                      <Link
                        to={`/admin/profile-edit-history?userId=${member._id}&username=${member.username}`}
                      >
                        Search this member's full history →
                      </Link>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </>
            ) : null}
          </div>
        </div>
      </Container>
    </div>
  );
}
